import { FC, useEffect, useMemo, useState } from "react";
import { RegisterPriceDataProvider } from "../../../../Services/DataProviders/RegisterPriceDataProvider";
import { useAppContext } from "../../../../system/providers/appContextProvider";
import { IRegisterPriceViewDTO } from "../../../../libs/coreapi-dto/dirs/registerPrice";
import { LoadingStatus } from "../../../../@types/enumsGlobal";
import useGridFilter, { CreateSelectorGridFilter } from "../../../../system/hooks/useGridFilter";
import { PluginWrapper } from "../../../../components/plugins/PluginWrapper";
import { Spinner } from "../../../../components/spiner/Spinner";
import { DefaultGrid } from "../../../../components/grids/default/defaultGrid";
import { ChangeableGrid } from "../../../../components/grids/changeableGrid/ChangeableGrid";

interface IRegisterPriceByGoodsGridProps extends IGridProps {
    goodsIdGlobal?: string;
}

const RegisterPriceByGoodsGrid: FC<IRegisterPriceByGoodsGridProps> = (props) => {
    const appCtx = useAppContext();
    const registerPriceDataProvider = new RegisterPriceDataProvider(appCtx.coreApiService);

    const [data, setData] = useState<IRegisterPriceViewDTO[]>([]);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(true);
    const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>(LoadingStatus.InProcess);
    const [totalCount, setTotalCount] = useState(0);
    const [viewState, setViewState] = useState<GridStateType>("view");
    const [selectedItem, setSelectedItem] = useState<IGridRow>();
    const CreateSelectorGridFilter: IGridFilter = {
        numberPerPage: 1000,
        pageNumber: 1,
        columnFilters: [
            {
                name: "deleted",
                operator: "Eq",
                value: false,
            },
        ]
    };
    const [gridFilter, dispatchGridFilter] = useGridFilter(CreateSelectorGridFilter);
    const idGoodsGlobal = props.goodsIdGlobal;

    const [sort, setSort] = useState<{ field: string; desc: boolean } | null>(null);

    const applySort = (rows: any[]) => {
        if (!sort) return rows;
        return [...rows].sort((a, b) => {
            const av = a[sort.field];
            const bv = b[sort.field];
            if (av == null && bv == null) return 0;
            if (av == null) return 1;
            if (bv == null) return -1;
            const cmp = typeof av === "number" && typeof bv === "number"
                ? av - bv
                : String(av).localeCompare(String(bv));
            return sort.desc ? -cmp : cmp;
        });
    };

    const sortedData = useMemo(() => applySort(data), [data, sort]);

    useEffect(() => {
        if (!idGoodsGlobal) return;

        setIsSubmitting(true);
        registerPriceDataProvider.getByGoods(idGoodsGlobal, '1900-01-01', (data) => {            
            setData(data);
            setTotalCount(data.length);
            setViewState('view');
            data.length > 0 ? setLoadingStatus(LoadingStatus.Completed) : setLoadingStatus(LoadingStatus.NoData);
            setIsSubmitting(false);
        });
    }, [viewState, gridFilter, idGoodsGlobal])

    return (
        <PluginWrapper>
            {
                isSubmitting ? <Spinner />
                    : <DefaultGrid
                        showQuantity
                        searching
                        multipleSelect={props.multipleSelect}
                        gridId={props.gridId}
                        data={sortedData}
                        loadingStatus={loadingStatus}
                        setLoadingStatus={setLoadingStatus}
                        totalCount={totalCount}
                        filter={{ ...gridFilter, fieldOrderBy: sort?.field, orderDesc: sort?.desc }}
                        onSort={(col) => {
                            setSort(prev =>
                                prev?.field === col.propertyName
                                    ? { field: col.propertyName, desc: !prev.desc }
                                    : { field: col.propertyName, desc: false }
                            );
                        }}
                        dataProvider={registerPriceDataProvider}
                        plugin={props.plugin}
                        separator
                        singleDirectory
                        selectedItem={selectedItem}
                        onDoubleClick={(row) => props.onDoubleClick?.(row)}
                        onSelect={(row) => {
                            setSelectedItem(row)
                            props.onSelect?.(row)
                        }}
                        onFilterDelete={(i) => dispatchGridFilter({ type: "deleteColumnFilter", payload: i.propertyName })}
                        onPageNumberChange={(n) => dispatchGridFilter({ type: "changePageNumber", payload: { pageNumber: n } })}
                        onNumberPerPageChange={(n) => dispatchGridFilter({ type: "changeNumberPerPage", payload: { numberPerPage: n } })}
                        onEnter={() => setViewState("edit")}
                    />

            }
        </PluginWrapper>
    )
}

export default RegisterPriceByGoodsGrid